import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import type { RouteObject } from 'react-router-dom'
import RequirePermission from '../components/RequirePermission'
import RequireSelfManaged from '../components/RequireSelfManaged'
import type { PermissionCode } from './permissions'
// ── ROUTE_PERMISSION compile-time guard ──────────────────────────
// Every route in ROUTES must declare its guard decision in its type:
// `permission` (the endpoint perm the page's API enforces), a typed
// `publicReason`, or a redirect — a new entry with none of those is a
// tsc error, not a review catch. This replaces the grep-based CI gate
// (scripts/check-route-guards.py), which could only see what the JSX
// spelled on one line.
//
// To add a route: append an entry to ROUTES with the permission its
// endpoint enforces (`permission: PERMS.X` — never a string literal).
// If no single perm fits, pick the honest `publicReason`, not the
// closest perm. Relative children inherit the parent's guard; give a
// child its own `permission` only to tighten past the parent.

/**
 * Why a route carries no RequirePermission. The old allowlist file's
 * categories, now typed — each use sites the reason at the route.
 */
export type PublicRouteReason =
  /** Reached without a session: login, SSO callback. Outside ProtectedRoute. */
  | 'front-door'
  /** Nothing beyond the viewer's own data: dashboard, profile, notifications. */
  | 'user-scoped'
  /** Patient portal: PortalLayout auth, not staff RBAC. */
  | 'portal-auth'
  /** /403: guarding the denied page would be circular. */
  | 'access-denied'
  /** Sections gate individually; no single route perm fits (inbox). */
  | 'aggregator'
  /** Catchall: must render for any visitor, including logged-out. */
  | 'not-found'

/** Absolute staff route, guarded by its endpoint permission. */
export interface GuardedRouteEntry {
  path: string
  permission: PermissionCode
  /** Also require customer-operated deployment (file-key licensing). */
  selfManagedOnly?: boolean
  element: ReactNode
  children?: ChildRouteEntry[]
  /** Never alongside `permission` — a route is guarded or public, not both. */
  publicReason?: never
}

/** Absolute route that is deliberately public, with the reason why. */
export interface PublicRouteEntry {
  path: string
  publicReason: PublicRouteReason
  element: ReactNode
  children?: ChildRouteEntry[]
  /** Never alongside `publicReason` — a route is guarded or public, not both. */
  permission?: never
}

/** Absolute redirect, e.g. `/` or a retired page. Rarely permissioned. */
export interface RedirectRouteEntry {
  path: string
  permission?: PermissionCode
  redirectTo: string
}

/** Index redirect (portal root). Inherits the parent guard. */
export interface IndexRedirectEntry {
  index: true
  redirectTo: string
}

/** Index element (chart default tab). Inherits the parent guard. */
export interface IndexElementEntry {
  index: true
  element: ReactNode
}

/** Pathless layout: auth/shell/suspense boundaries with nested routes. */
export interface LayoutRouteEntry {
  element: ReactNode
  children: RouteEntry[]
}

/**
 * Relative child: inherits the parent guard. `permission` tightens past
 * it (chart `medications/new`). Absolute paths are rejected at build —
 * nested under a layout they would render with no RequirePermission of
 * their own, silently unguarded (see mapChild).
 */
export type ChildRouteEntry =
  | {
      path: string
      permission?: PermissionCode
      element: ReactNode
      children?: ChildRouteEntry[]
    }
  | IndexRedirectEntry
  | IndexElementEntry

export type RouteEntry =
  | GuardedRouteEntry
  | PublicRouteEntry
  | RedirectRouteEntry
  | IndexRedirectEntry
  | IndexElementEntry
  | LayoutRouteEntry

function mapChild(child: ChildRouteEntry): RouteObject {
  if ('redirectTo' in child) {
    return { index: true, element: <Navigate to={child.redirectTo} replace /> }
  }
  if ('index' in child) {
    return { index: true, element: child.element }
  }
  if (child.path.startsWith('/')) {
    // The one shape the types cannot forbid: a child path is just a
    // string, and an absolute one nested here would render inside the
    // parent layout with NO RequirePermission of its own. Fail closed
    // at router build instead of shipping an unguarded page — absolute
    // routes belong at the top level with an explicit decision.
    throw new Error(
      `Route "${child.path}" is absolute — nested routes inherit the parent guard and must use relative paths.`,
    )
  }
  const element =
    child.permission === undefined ? (
      child.element
    ) : (
      <RequirePermission permission={child.permission}>
        {child.element}
      </RequirePermission>
    )
  return {
    path: child.path,
    element,
    ...(child.children === undefined
      ? {}
      : { children: child.children.map(mapChild) }),
  }
}

function mapEntry(entry: RouteEntry): RouteObject {
  if ('redirectTo' in entry) {
    const target = <Navigate to={entry.redirectTo} replace />
    const element =
      'permission' in entry && entry.permission !== undefined ? (
        <RequirePermission permission={entry.permission}>
          {target}
        </RequirePermission>
      ) : (
        target
      )
    return 'index' in entry
      ? { index: true, element }
      : { path: entry.path, element }
  }
  if ('index' in entry) {
    return { index: true, element: entry.element }
  }
  if (!('path' in entry)) {
    return { element: entry.element, children: toRouteObjects(entry.children) }
  }
  // `!== undefined`, not `in`: PublicRouteEntry declares
  // `permission?: never`, so `in` cannot narrow the union.
  if (entry.permission !== undefined) {
    const guarded = (
      <RequirePermission permission={entry.permission}>
        {entry.element}
      </RequirePermission>
    )
    // RequireSelfManaged stays OUTSIDE RequirePermission: deployment
    // mode is checked before the grant, matching the old JSX nesting.
    const element =
      entry.selfManagedOnly === true ? (
        <RequireSelfManaged>{guarded}</RequireSelfManaged>
      ) : (
        guarded
      )
    return {
      path: entry.path,
      element,
      ...(entry.children === undefined
        ? {}
        : { children: entry.children.map(mapChild) }),
    }
  }
  return {
    path: entry.path,
    element: entry.element,
    ...(entry.children === undefined
      ? {}
      : { children: entry.children.map(mapChild) }),
  }
}

export const toRouteObjects = (entries: RouteEntry[]): RouteObject[] =>
  entries.map(mapEntry)
